/**
 * leadReadiness.ts — the qualification question's vocabulary and patient-facing
 * copy. Formerly `leadIntent.ts`, and the rename is the point of this file.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * ── WHY "READINESS" AND NOT "INTENT" (CTO, 2026-09-12: "call it something
 *    else") ──────────────────────────────────────────────────────────────────
 * "Intent" is CRM jargon. It names what the BUSINESS wants out of the answer
 * (purchase intent, to segment a funnel), not what the PATIENT is telling us,
 * and no patient has ever described themselves as "an intent". "Readiness" is
 * the patient's own frame — how ready they are to start — and it is the word
 * the four options were already written in ("I'm ready to start" … "I'm just
 * looking for now").
 *
 * The rename also does real compliance work, which is why it is worth the
 * churn. "Readiness" is a word about a DECISION; it cannot be completed by a
 * fact about someone's body or their treatment. A softer name — "about you",
 * "your situation", "your background" — invites exactly the health-shaped
 * options this vocabulary exists to keep out, and it invites them from someone
 * who has not read the header below. The name is the first guard.
 *
 * ⚠️ THE WIRE FIELD IS STILL `intent`, AND MUST STAY THAT WAY. The rename is
 * patient-facing and portal-internal ONLY. clinic-service owns the vocabulary
 * as `LEAD_INTENTS` and stores it on `Lead.intent`; the column, the staff
 * labels, the board filter and the Insights cut are all live on prod under that
 * name, and every Webflow embed already in the wild posts `intent` on the lead
 * capture. Renaming the wire would drift the two for nothing. Keys: unchanged.
 *
 * ⚠️ THE KEYS MIRROR clinic-service's `LEAD_INTENTS` AND MUST NOT DRIFT. They
 * are deliberately hard-coded here rather than fetched: serving the vocabulary
 * as config would put an unthrottled public request on every marketing-page
 * mount, and a failed fetch would silently show no question at all to the
 * majority of leads. The client renders; the SERVER decides what is valid
 * (R41) — an unknown key is rejected by the wire enum, and a legacy value is
 * nulled on the way back out.
 *
 * ⚠️ AND NOTHING HEALTH-RELATED MAY JOIN THIS LIST. The option the CTO
 * originally asked for — "already on weight-loss medication" — is excluded for
 * two independent reasons, both of which bite HERE rather than server-side,
 * because this is the copy a member of the public reads before any eligibility
 * decision has been made about them:
 *
 *   · R28 / MHRA. Naming a prescription-only medicine to someone who has not
 *     passed any eligibility gate is advertising it. There is no compliance
 *     guard in front of this component — the questionnaire's
 *     `assertPreEligibilityPomClean` protects the questionnaire, not this — so
 *     this file is the boundary.
 *   · UK GDPR Art 9. It is health data captured before any clinical
 *     relationship, landing on a commercial record that sales staff read.
 *
 * ── WHERE THE CTO'S OTHER TWO QUESTIONS LIVE (2026-09-12) ───────────────────
 * The CTO asked for a three-question mini form before the questionnaire:
 *   1. new to weight-loss medication, or an existing user?
 *   2. how soon do you need this — ready to buy, or still shopping around?
 *   3. if existing: who are you with, and roughly what do you spend?
 *
 * Only (2) may be asked here, and (2) is exactly the vocabulary below. (1) and
 * the "who are you with" half of (3) ARE the questionnaire's prior-treatment
 * question, which clinic-service serves only AFTER the BMI/eligibility gate
 * (`surface: 'assessment'`, because its options name prescription-only
 * medicines). Asking them here would move them in front of that gate, which is
 * the precise thing this file exists to prevent — and clinic-service's
 * `lead-intent.spec.ts` fails the build if a health-shaped word enters the
 * vocabulary, so it would not even compile as a change.
 *
 * The "roughly what do you spend" half of (3) is asked NOWHERE today. It reads
 * commercial, but it is only answerable by someone who has just disclosed that
 * they are on treatment with a named provider, so it inherits its neighbour's
 * Art 9 character and belongs in the same post-gate block. Adding it is a
 * clinic-service questionnaire change, flagged to the CTO rather than invented
 * here. DO NOT "fix" that by adding it below.
 */

/**
 * The four options, highest declared readiness first.
 *
 * Keys are clinic-service's `LEAD_INTENTS`, in its order — the board and
 * Insights read them this way so a lane of ready-to-buy leads scans first.
 */
export const READINESS_OPTIONS = [
  { key: 'ready_to_start', label: "I'm ready to start" },
  { key: 'checking_eligibility', label: 'I want to check if I can have it' },
  { key: 'comparing_prices', label: "I'm comparing options and prices" },
  { key: 'researching', label: "I'm just looking for now" },
] as const;

export type LeadReadinessKey = (typeof READINESS_OPTIONS)[number]['key'];

/**
 * The question a patient reads.
 *
 * Deliberately about what they want to DO, not about what is true of them —
 * which is the same line the vocabulary holds, expressed in the prompt. The
 * CTO's phrasing was "how soon do you need this — ready to buy, or still
 * shopping around"; the four answers below are that spectrum, so the prompt
 * asks it once and the options carry the rest.
 *
 * REQUIRED ON THE SCREEN SINCE 2026-09-25 (CTO: *"They shouldn't be able to
 * skip this question, so as soon as they select an answer for one of these
 * things, it should take them to the next page."*). Until then it was
 * optional, on the argument that a mandatory field here costs more conversion
 * than the answer is worth; the CTO has made that trade the other way, and
 * `QualificationStep.tsx` records the new behaviour. Two things from the old
 * rule still hold:
 *   - STILL NO DEFAULT. Required means the patient chooses; it never means we
 *     choose for them. Nothing is pre-selected on a first visit, because a
 *     default would manufacture a segment out of everyone who did not read the
 *     question and poison the exact comparison the field exists for.
 *   - THE WIRE STAYS OPTIONAL. Lanes that never show this screen (the plan
 *     link, a cold `?assessment=` restore) carry no answer, a Webflow embed's
 *     capture may or may not post `intent`, and a patient can still leave this
 *     screen by Back or by closing the tab. clinic-service must keep accepting
 *     a lead with no answer; only this screen stopped offering a skip.
 */
export const READINESS_QUESTION = 'How soon are you looking to start?';
/*
 * NO LINE UNDER THE QUESTION (Ashley Haveloch-Jones, via the CTO, 2026-09-26:
 * "unneeded text"). The question and its four answers say everything the
 * screen needs; the help line that sat between them ("Choose the one that fits
 * best. It helps our team know how to help you.") is gone, and
 * `qualificationFlow.spec` keeps it gone. If a line ever comes back, the two
 * rules it lived under still hold: never "optional" or "skip" (the question is
 * required, above), and never a promise that the answer changes what the
 * patient is shown — nothing does; the only reader is our team
 * (`READINESS_STAFF_LABELS` below).
 */

/**
 * The same four answers, in STAFF words, for the deal drawer's journey rail.
 *
 * Keyed on `LeadReadinessKey`, so the compiler proves this covers exactly the
 * options the patient is offered — a fifth option cannot be added to the screen
 * without a label appearing here too.
 *
 * ⚠️ SEPARATE FROM THE PATIENT COPY ON PURPOSE. The patient reads "I'm just
 * looking for now" in their own voice; a staff board says what it means about
 * the record. Reusing one string for both is how a patient-voice sentence ends
 * up on an internal screen, or worse, the other way round.
 */
export const READINESS_STAFF_LABELS: Record<LeadReadinessKey, string> = {
  ready_to_start: 'Ready to start',
  checking_eligibility: 'Checking whether they can have it',
  comparing_prices: 'Comparing options and prices',
  researching: 'Looking for now',
};
